import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Screen-level error boundary. A render error in one screen should surface a
 * readable message (and a way to recover) rather than unmounting the whole app
 * and leaving a blank page. Reset it by changing `resetKey` (we key it on the
 * current route, so navigating away clears the error).
 */
interface Props {
  resetKey: string;
  children: ReactNode;
}
interface State {
  error: Error | null;
  lastKey: string;
}

export class ScreenErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, lastKey: this.props.resetKey };

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    // Clear the error when the route changes.
    if (props.resetKey !== state.lastKey) return { error: null, lastKey: props.resetKey };
    return null;
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaced in the console for debugging; never logs credentials.
    console.error("Screen error:", error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div style={{ maxWidth: 1320, margin: "0 auto", padding: "2rem" }}>
          <div role="alert" style={{ background: "#fff", border: "1px solid #f3c2c2", borderRadius: 12, padding: "1.5rem", color: "#8a1f1f" }}>
            <h2 style={{ margin: "0 0 6px", fontSize: "1.1rem" }}>This screen hit an error</h2>
            <p style={{ margin: "0 0 12px", fontSize: "0.9rem", color: "#7a2a2a" }}>{this.state.error.message}</p>
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              style={{ background: "#1d6fb8", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontWeight: 600 }}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
